import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { media } from '../../styles/breakpoints';

export interface OrgPublication {
  id: number;
  name: string;
  account_id?: number;
}

export interface OrgAccount {
  id: number;
  name: string;
}

interface OrgPublicationBarProps {
  accounts: OrgAccount[];
  publications: OrgPublication[];
  currentAccountName?: string;
  currentPublicationName?: string;
  onPublicationChange: (publicationId: number) => void;
  onAccountChange?: (accountId: number) => void;
  /** When true, only the account/organization section is shown (no publication dropdown). */
  hidePublication?: boolean;
  /**
   * Org-level "All publications" target. When set, an "All publications" item is
   * rendered at the top of the publication dropdown (and the dropdown/caret shows
   * even with a single publication). Restores classic's org-settings entry point
   * (eng-maintenance#21483). Server-authoritative: pass it through only when the
   * BE emits `all_publications_path` for the current settings surface.
   */
  allPublicationsHref?: string | null;
  /**
   * When true, the publication segment renders with "All publications" as the
   * selected value (used on org-level pages instead of hiding the segment).
   */
  allPublicationsSelected?: boolean;
}

const BarContainer = styled.div`
  display: flex;
  align-items: center;
  background: #0094ab;
  padding: 0;
  font-size: 13px;
  color: white;
  position: relative;
  z-index: 5;

  ${media.orgNav} {
    flex-wrap: wrap;
  }
`;

const NavItem = styled.div<{ $hasDropdown?: boolean }>`
  position: relative;
  padding: 8px 20px;
  cursor: ${props => (props.$hasDropdown ? 'pointer' : 'default')};
  user-select: none;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${props => (props.$hasDropdown ? 'rgba(0,0,0,0.1)' : 'transparent')};
  }

  ${media.orgNav} {
    width: 100%;
    box-sizing: border-box;
  }
`;

const NavLabel = styled.span`
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #93f2fb;
  margin-right: 4px;
`;

const NavValue = styled.span`
  font-weight: 700;
  text-transform: uppercase;
  font-size: 12px;
`;

const NavDivider = styled.div`
  width: 1px;
  background: #007a8d;
  align-self: stretch;

  ${media.orgNav} {
    width: 100%;
    height: 1px;
  }
`;

const Caret = styled.span<{ $open?: boolean }>`
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid white;
  transition: transform 0.2s;
  transform: ${props => (props.$open ? 'rotate(180deg)' : 'none')};
`;

const Dropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 250px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  list-style: none;
  padding: 4px 0;
  margin: 0;
  z-index: 100;
  max-height: 400px;
  overflow-y: auto;
`;

const DropdownItem = styled.li<{ $active?: boolean }>`
  padding: 8px 16px;
  font-size: 13px;
  color: ${props => (props.$active ? '#0094ab' : '#333')};
  font-weight: ${props => (props.$active ? '600' : '400')};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f5f5f5;
  }

  &:focus {
    background: #f5f5f5;
    outline: 2px solid #0094ab;
    outline-offset: -2px;
  }
`;

export default function OrgPublicationBar({
  accounts,
  publications,
  currentAccountName,
  currentPublicationName,
  onPublicationChange,
  onAccountChange,
  hidePublication,
  allPublicationsHref,
  allPublicationsSelected,
}: OrgPublicationBarProps) {
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showPubDropdown, setShowPubDropdown] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const pubRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setShowAccountDropdown(false);
      }
      if (pubRef.current && !pubRef.current.contains(e.target as Node)) {
        setShowPubDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasMultipleAccounts = accounts.length > 1;
  const hasMultiplePublications = publications.length > 1;
  const hasAllPublications = !!allPublicationsHref;
  // Show the publication dropdown (caret) when there's more than one pub, an
  // "All publications" entry to offer, or we're on an org-level page (where the
  // dropdown lets you jump back DOWN to a publication). Mirrors classic
  // `show_dropdown?` = multiple_pubs? || show_all_publications_link? || allow_single_pub_dropdown?.
  const showPublicationDropdown =
    hasMultiplePublications || hasAllPublications || !!allPublicationsSelected;
  // On org-level pages the selected value is "All publications".
  const selectedPublicationLabel = allPublicationsSelected
    ? 'ALL PUBLICATIONS'
    : currentPublicationName?.toUpperCase();

  const goToAllPublications = () => {
    if (allPublicationsHref) window.location.href = allPublicationsHref;
  };

  const handleDropdownItemKeyDown = (
    e: React.KeyboardEvent,
    action: () => void
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <BarContainer>
      {hasMultipleAccounts ? (
        <NavItem
          ref={accountRef}
          $hasDropdown
          onClick={() => setShowAccountDropdown(!showAccountDropdown)}
        >
          <NavLabel>Organization</NavLabel>
          <NavValue>{currentAccountName?.toUpperCase()}</NavValue>
          <Caret $open={showAccountDropdown} />
          {showAccountDropdown && (
            <Dropdown role="listbox">
              {accounts.map(account => (
                <DropdownItem
                  key={account.id}
                  role="option"
                  tabIndex={0}
                  $active={account.name === currentAccountName}
                  aria-selected={account.name === currentAccountName}
                  onClick={() => {
                    onAccountChange?.(account.id);
                    setShowAccountDropdown(false);
                  }}
                  onKeyDown={(e) => handleDropdownItemKeyDown(e, () => {
                    onAccountChange?.(account.id);
                    setShowAccountDropdown(false);
                  })}
                >
                  {account.name}
                </DropdownItem>
              ))}
            </Dropdown>
          )}
        </NavItem>
      ) : currentAccountName ? (
        <NavItem>
          <NavValue>{currentAccountName.toUpperCase()}</NavValue>
        </NavItem>
      ) : null}

      {!hidePublication && currentAccountName && <NavDivider />}

      {!hidePublication && (showPublicationDropdown ? (
        <NavItem
          ref={pubRef}
          $hasDropdown
          onClick={() => setShowPubDropdown(!showPubDropdown)}
        >
          <NavLabel>Publication</NavLabel>
          <NavValue>{selectedPublicationLabel}</NavValue>
          <Caret $open={showPubDropdown} />
          {showPubDropdown && (
            <Dropdown role="listbox">
              {hasAllPublications && (
                <DropdownItem
                  key="all-publications"
                  role="option"
                  tabIndex={0}
                  $active={!!allPublicationsSelected}
                  aria-selected={!!allPublicationsSelected}
                  onClick={() => {
                    goToAllPublications();
                    setShowPubDropdown(false);
                  }}
                  onKeyDown={(e) => handleDropdownItemKeyDown(e, () => {
                    goToAllPublications();
                    setShowPubDropdown(false);
                  })}
                >
                  All publications
                </DropdownItem>
              )}
              {publications.map(pub => (
                <DropdownItem
                  key={pub.id}
                  role="option"
                  tabIndex={0}
                  $active={!allPublicationsSelected && pub.name === currentPublicationName}
                  aria-selected={!allPublicationsSelected && pub.name === currentPublicationName}
                  onClick={() => {
                    onPublicationChange(pub.id);
                    setShowPubDropdown(false);
                  }}
                  onKeyDown={(e) => handleDropdownItemKeyDown(e, () => {
                    onPublicationChange(pub.id);
                    setShowPubDropdown(false);
                  })}
                >
                  {pub.name}
                </DropdownItem>
              ))}
            </Dropdown>
          )}
        </NavItem>
      ) : (
        <NavItem>
          <NavLabel>Publication</NavLabel>
          <NavValue>{selectedPublicationLabel}</NavValue>
        </NavItem>
      ))}
    </BarContainer>
  );
}
